import { Apollo, gql } from 'apollo-angular';
import { Injectable } from '@angular/core';
import { BaseRepository } from 'src/app/services/remote-data-service/repositories/base.repository';
import { ClientMapper } from 'src/app/services/remote-data-service/mappers/client.mapper';
import { ClientDto } from 'src/app/services/remote-data-service/dtos/client.dto';
import { Client } from 'src/app/entities/client/client';
import { catchError, map } from 'rxjs';
import log from 'loglevel';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';


/**
 * GraphQL arguments for `get_clients` query.
 */
export interface ClientsFilters {
  page?: number;
  limit?: number;
  name?: string;
  groups?: string[];
}


/**
 * GraphQL query used by this gateway.
 *
 * Exported because is required by tests.
 */
@Injectable({
  providedIn: 'root',
})
export class ClientRepository extends BaseRepository<Client, ClientDto> {
  protected override _mapper = new ClientMapper();
  protected override dataPropertyName = 'get_clients';

  protected override logger = log.getLogger('ClientRepository');

  constructor(
    protected override apollo: Apollo,
    protected override snackBar: SnackbarService
  ) {
    super(apollo, snackBar);
  }

  /******************/
  /* Public methods */
  /******************/
  public override getGqlQuery() {
    return gql`
      query GetClients($page: Int, $limit: Int, $name: String, $groups: [ID]) {
        get_clients(page: $page, limit: $limit, name: $name, groups: $groups) {
            items {
                name
                uuid
            }
            total
            pager {
              current
              next
              prev
            }
        }
      }
    `;
  }


  /**
   * Function to add new clients
   * @param client
   * @returns A boolean observable that indicated if query was successful.
   */
  public addNewClient(client: Client) {
    const ADDCLIENT = gql`
      mutation MyMutation {
        create_client(data: {uuid: "${client.id}", name: "${client.name}", groups: []}) {
          client {
            uuid
            name
            groups {
              name
            }
            permissions {
              update
              delete
            }
          }
          errors
        }
      }
    `;

    return this.apollo
      .mutate({
        mutation: ADDCLIENT,
        variables: {},
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          this.logger.error(error);
          this.snackBar.showError(error);
          return [];
        })
      );
  }


  public updateClient(client: Client) {
    const UPDATECLIENT = gql`
      mutation MyMutation {
        update_client(
          uuid: "${client.id}",
          data: {name: "${client.name}"}
        ) {
          client {
            uuid
            name
          }
          errors
        }
      }
    `;

    return this.apollo
      .mutate({
        mutation: UPDATECLIENT,
        variables: {},
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          this.logger.error(error);
          this.snackBar.showError(error);
          return [];
        })
      );
  }


  public removeClient(client: Client) {
    const REMOVECLIENT = gql`
      mutation MyMutation {
        delete_client(uuid: "${client.id}") {
          client {
            name
          }
          errors
        }
      }
    `;

    return this.apollo
      .mutate({
        mutation: REMOVECLIENT,
        variables: {},
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          this.logger.error(error);
          this.snackBar.showError(error);
          return [];
        })
      );
  }
}
